import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';

import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import {
  buildPanelPath,
  parseSttokHref,
} from 'modules/ai/utils/resolveAiPanelLink';

type AiMarkdownLinkProps = {
  href?: string;
  children?: ReactNode;
  societyId: string;
  conversationId?: string | null;
  messageId?: string;
};

const AiMarkdownLink: FC<AiMarkdownLinkProps> = ({
  href,
  children,
  societyId,
  conversationId = null,
  messageId,
}) => {
  const mixpanel = useMixpanel();
  const sttokLink = parseSttokHref(href);

  const trackClick = (routeId: string, linkTarget: string) => {
    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.PANEL_LINK_CLICKED,
      extras: {
        routeId,
        linkTarget,
        linkRaw: href,
        linkLabel: typeof children === 'string' ? children : '',
        conversationId,
        messageId,
      },
    });
  };

  if (sttokLink) {
    const path = buildPanelPath(sttokLink.routeId, { societyId });
    if (path) {
      return (
        <Link
          to={path}
          className="navi-panel-link"
          onClick={() => trackClick(sttokLink.routeId, path)}
        >
          <em className="icon ni ni-link navi-panel-link__icon" aria-hidden />
          <span className="navi-panel-link__text">{children}</span>
        </Link>
      );
    }
    return <span>{children}</span>;
  }

  // eslint-disable-next-line no-script-url
  if (!href || href.startsWith('javascript:')) {
    return <span>{children}</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackClick('external', href)}
    >
      {children}
    </a>
  );
};

export default AiMarkdownLink;
